import { Injectable } from '@angular/core';
import {
  DrilldownContainerChartSize,
  KpiTimeCycleChartData,
  TimeCycleLabels,
  TimeCycleTooltipInfo
} from '@models';

@Injectable({
  providedIn: 'root'
})
export class TimeCycleChartTooltipService {
  getTooltipInfo(
    chartData: KpiTimeCycleChartData,
    size: DrilldownContainerChartSize,
    labelValue: string | undefined,
    dataIndex?: number
  ): TimeCycleTooltipInfo | null {
    const reversedLabels = chartData.labels.toReversed();
    const reversedWeeks = chartData.timeWeeks.toReversed();
    const reversedCategory = chartData.category.toReversed();

    let resolvedIndex = Number.isInteger(dataIndex) ? (dataIndex as number) : -1;
    if (resolvedIndex < 0) {
      if (!labelValue) {
        return null;
      }
      resolvedIndex = reversedLabels.findIndex((labels: TimeCycleLabels) =>
        size === 'small' ? labels.small === labelValue : labels.large === labelValue
      );
    }

    if (resolvedIndex < 0 || resolvedIndex >= reversedLabels.length) {
      return null;
    }

    return {
      title: reversedLabels[resolvedIndex].large,
      weeks: reversedWeeks[resolvedIndex],
      status: reversedCategory[resolvedIndex] ?? null
    };
  }

  getTooltipXPosition(
    pointerX: number,
    source: 'bar' | 'yAxis',
    tooltipWidth: number,
    tooltipGap: number,
    viewportWidth: number
  ): number {
    const clampToViewport = (x: number): number => {
      if (viewportWidth <= 0) {
        return Math.max(x, 0);
      }

      const maxLeft = Math.max(viewportWidth - tooltipWidth, 0);
      return Math.min(Math.max(x, 0), maxLeft);
    };

    if (source === 'yAxis') {
      return clampToViewport(pointerX + tooltipGap);
    }

    return clampToViewport(pointerX - tooltipWidth - tooltipGap);
  }

  private getClientCoordinates(nativeEvt: any): { pointerX: number; pointerY: number } | null {
    if (typeof nativeEvt?.clientX === 'number' && typeof nativeEvt?.clientY === 'number') {
      return {
        pointerX: nativeEvt.clientX,
        pointerY: nativeEvt.clientY
      };
    }
    return null;
  }

  private getLocalCoordinates(
    evt: any,
    nativeEvt: any,
    rect: DOMRect | undefined
  ): { pointerX: number; pointerY: number } {
    const localPointerX = evt?.zrX ?? nativeEvt?.offsetX ?? evt?.offsetX ?? 0;
    const localPointerY = evt?.zrY ?? nativeEvt?.offsetY ?? evt?.offsetY ?? 0;

    return {
      pointerX: (rect?.left ?? 0) + localPointerX,
      pointerY: (rect?.top ?? 0) + localPointerY
    };
  }

  getPointerCoordinates(
    params: any,
    wrapperEl?: HTMLElement
  ): { pointerX: number; pointerY: number } {
    const evt = params?.event;
    const nativeEvt = evt?.event ?? evt;

    const clientCoordinates = this.getClientCoordinates(nativeEvt);
    if (clientCoordinates) {
      return clientCoordinates;
    }

    const rect = wrapperEl?.getBoundingClientRect();
    return this.getLocalCoordinates(evt, nativeEvt, rect);
  }
}
